<template>
    <div class="relative -mb-8">
        <div class="flex bg-gray-100" style="height: 80vh; overflow: hidden;">

            <!-- Mobile Overlay -->
            <div v-if="isSidebarOpen" class="fixed inset-0 bg-black bg-opacity-50 z-40 md:hidden"
                @click="toggleSidebar">
            </div>

            <!-- Sidebar -->
            <div :class="[
                'fixed md:relative w-3/4 md:w-1/4 bg-white border-r border-gray-200 z-50 h-full transition-transform duration-300 ease-in-out transform',
                isSidebarOpen ? 'translate-x-0' : '-translate-x-full md:translate-x-0'
            ]">
                <!-- Close Button for Mobile -->
                <button v-if="isSidebarOpen"
                    class="absolute top-2 right-2 p-2 text-gray-600 hover:text-gray-900 md:hidden"
                    @click="toggleSidebar">
                    <span class="text-xl">×</span>
                </button>
                <!-- Tab Buttons -->
                <div class="w-full flex sticky top-0  border-gray-200 bg-white pt-2">
                    <button @click="name = 'orders'" :class="[
                        'p-3 w-full text-center text-lg font-semibold transition-all duration-300 outline-none rounded-t-lg',
                        name === 'orders'
                            ? 'text-orange-500 bg-white border border-b-0 border-gray-200'
                            : 'text-gray-600 hover:text-orange-500 border-b hover:bg-gray-50'
                    ]">
                        Orders
                    </button>
                    <button @click="name = 'shipping'" :class="[
                        'p-3 w-full text-center text-lg font-semibold transition-all duration-300 outline-none rounded-t-lg',
                        name === 'shipping'
                            ? 'text-orange-500 bg-white border border-b-0 border-gray-200 '
                            : 'text-gray-600 hover:text-orange-500 border-b hover:bg-gray-50'
                    ]">
                        Shipping
                    </button>

                </div>


                <!-- Orders Tab Content -->
                <div v-if="name == 'orders'" class="overflow-y-auto h-full pt-3 pb-12">
                    <!-- Loader -->
                    <div v-if="loading && orders.length === 0"
                        class="flex justify-center items-center text-orange-500 h-full">
                        loading...
                    </div>

                    <!-- Order List -->
                    <template v-else>
                        <div v-if="orders.length === 0" class="p-4 flex justify-center items-center h-full">
                            <div class="text-center text-gray-500">No incoming messages</div>
                        </div>

                        <div v-else v-for="order in orders" :key="order.id" class="p-2 hover:bg-gray-50 cursor-pointer"
                            @click="selectOrder(order)">
                            <div class="flex items-center p-4 rounded-md"
                                :class="{'bg-orange-100': hasUnreadMessages(order),
                                        'bg-orange-200': activeOrder === order._id }">

                                <div class="w-10 h-10 bg-gray-300 rounded-full flex items-center justify-center">
                                    <img src="https://img.icons8.com/color/96/circled-user-male-skin-type-1-2--v1.png"
                                        alt="User avatar" class="w-full h-full object-cover" />
                                </div>
                                <div class="ml-3">
                                    <p class="font-medium">Order ID: {{ order.id }}</p>
                                    <p class="text-sm text-gray-500">{{
                                        order.messages && order.messages.length > 0
                                            ? order.messages[order.messages.length - 1].message
                                            : 'No messages'
                                    }}</p>
                                </div>
                                <!-- Unread Message Indicator -->
                                <span v-if="hasUnreadMessages(order)"
                                    class="ml-auto w-2 h-2 bg-red-500 rounded-full animate-gentle-pulse"></span>
                            </div>
                        </div>
                    </template>
                </div>

                <!-- Shipping Tab Content -->
                <div v-if="name == 'shipping'" class="overflow-y-auto h-full pt-3 pb-12">
                    <!-- Loader -->
                    <div v-if="loading && shipping.length === 0"
                        class="flex justify-center text-orange-500 items-center h-full text-orange-50">
                        loading...
                    </div>

                    <!-- Shipping List -->
                    <template v-else>
                        <div v-if="shipping.length === 0" class="p-2 flex justify-center items-center h-full">
                            <div class="text-center text-gray-500">No shipping updates</div>
                        </div>

                        <div v-else v-for="shipment in shipping" :key="shipment.id"
                            class="p-2 hover:bg-gray-50 cursor-pointer overflow-y-auto"
                            @click="selectShipment(shipment)">
                            <div class="flex items-center p-4 rounded-md"
                                :class="{'bg-orange-100': hasUnreadMessages(shipment),
                                        'bg-orange-200' : activeOrder === shipment._id 
                                    }">
                            
                                <div class="w-10 h-10 bg-gray-300 rounded-full flex items-center justify-center">
                                    <img src="https://img.icons8.com/color/96/circled-user-male-skin-type-1-2--v1.png"
                                        alt="User avatar" class="w-full h-full object-cover" />
                                </div>
                                <div class="ml-3">
                                    <p class="font-medium">Shipment ID: {{ shipment.orderObjet.id }}</p>
                                    <p class="text-sm text-gray-500">{{
                                        shipment.messages && shipment.messages.length > 0
                                            ? shipment.messages[shipment.messages.length - 1].message
                                        : 'No messages'
                                        }}</p>
                                </div>
                                <!-- Unread Message Indicator -->
                                <span v-if="hasUnreadMessages(shipment)"
                                    class="ml-auto w-2 h-2 bg-red-500 rounded-full animate-gentle-pulse"></span>
                            </div>
                        </div>
                    </template>
                </div>
            </div>

            <!-- Main Chat Window -->
            <div class="relative flex-1 flex flex-col w-full md:w-3/4">
                <!-- Chat Header -->
                <div class=" flex justify-between top-0 p-4 border-b border-gray-200 bg-white  ">

                    <div>
                        <h2 v-if="name == 'orders' && activeOrderDetails.messages" class="text-xl font-semibold">Order
                            ID:
                            {{ activeOrderDetails.id }}</h2>
                        <h2 v-if="name == 'shipping' && activeShippingDetails.messages" class="text-xl font-semibold">
                            Shipping order ID: {{ activeShippingDetails.id }}</h2>
                    </div>
                    <button class="md:hidden mr-4 text-gray-600 hover:text-orange-500" @click="toggleSidebar">
                        <i class="material-icons">menu</i>
                    </button>
                </div>

                <!-- Chat Messages -->
                <div v-if="name == 'orders'" class="flex-1 overflow-y-auto p-4 bg-gray-50" ref="msgContainer">
                    <div class="flex justify-center items-center h-full"
                        v-if="!activeOrderDetails.messages || Object.keys(activeOrderDetails.messages).length === 0">
                        <div class="text-center text-gray-500">Select a conversation. Or start a new one!</div>
                    </div>
                    <div v-for="(messagesGroup, date) in activeOrderDetails.messages" :key="date" class="mb-4">

                        <!-- Date separator -->
                        <div class="flex items-center justify-center my-4">
                            <div class="bg-gray-200 rounded-full px-4 py-1">
                                <span class="text-sm text-gray-600">{{ date }}</span>
                            </div>
                        </div>



                        <!-- Messages for this date -->
                        <div v-for="message in messagesGroup" :key="message._id" class="flex relative mb-4">


                            <!-- Left Aligned -->
                            <div v-if="message.access.fullName !== currentUser.fullName" class="flex items-start">

                                <div class="w-10 h-10 rounded-full overflow-hidden mr-3">
                                    <img src="https://storeno.b-cdn.net/stores/9-2024/1727428835050.png"
                                        alt="User avatar" class="w-full h-full object-cover" />
                                </div>
                                <div class="bg-white p-3 rounded-lg shadow-sm max-w-md">
                                    <p class="font-medium text-xs text-gray-500 mb-2">{{ message.access.fullName }}</p>
                                    <p v-if="message.type === 'text'" class="text-gray-800">{{ message.message }}</p>
                                    <expandable-image v-else :src="mess.message" alt="img-pr" class="rounded-lg" />
                                    <span class="block text-xs text-gray-500 mt-1">{{ message.__v }}</span>
                                </div>
                            </div>

                            <!-- Right Aligned (Current User) -->
                            <div v-if="message.access.fullName === currentUser.fullName" class="flex items-end ml-auto">
                                <div class="bg-orange-500 p-3 rounded-lg shadow-sm max-w-md">
                                    <p class="font-medium text-xs text-gray-300 mb-2">{{ message.access.fullName }}</p>
                                    <p v-if="message.type === 'text'" class="text-white">{{ message.message }}</p>
                                    <expandable-image v-else :src="message.message" alt="img-pr" class="rounded-lg" />
                                    <span class="block text-xs text-orange-200 mt-1">{{ message.__v }}</span>
                                </div>
                                <div class="w-10 h-10 rounded-full overflow-hidden ml-3">
                                    <img src="https://img.icons8.com/color/96/circled-user-male-skin-type-1-2--v1.png"
                                        alt="User avatar" class="w-full h-full object-cover" />
                                </div>
                            </div>

                        </div>



                    </div>


                </div>

                <!-- Chat Messages -->
                <div v-if="name == 'shipping'" class="flex-1 overflow-y-auto p-4 bg-gray-50" ref="msgContainer">
                    <div class="flex justify-center items-center h-full"
                        v-if="!activeShippingDetails.messages || Object.keys(activeShippingDetails.messages).length === 0">
                        <div class="text-center text-gray-500">Select a conversation. Or start a new one!</div>
                    </div>
                    <div v-for="(messagesGroup, date) in activeShippingDetails.messages" :key="date" class="  mb-4">


                        <!-- Date separator -->
                        <div class="flex items-center justify-center my-4">
                            <div class="bg-gray-200 rounded-full px-4 py-1">
                                <span class="text-sm text-gray-600">{{ date }}</span>
                            </div>
                        </div>

                        <div v-for="message in messagesGroup" :key="message._id" class="flex relative mb-4">
                            <!-- Left Aligned -->
                            <div v-if="message.access.fullName !== currentUser.fullName" class="flex items-start">

                                <div class="w-10 h-10 rounded-full overflow-hidden mr-3">
                                    <img src="https://storeno.b-cdn.net/stores/9-2024/1727428835050.png"
                                        alt="User avatar" class="w-full h-full object-cover" />
                                </div>
                                <div class="bg-white p-3 rounded-lg shadow-sm max-w-md">
                                    <p class="font-medium text-xs text-gray-500 mb-2">{{ message.access.fullName }}</p>
                                    <p v-if="message.type === 'text'" class="text-gray-800">{{ message.message }}</p>
                                    <expandable-image v-else :src="mess.message" alt="img-pr" class="rounded-lg" />
                                    <span class="block text-xs text-gray-500 mt-1">{{ message.__v }}</span>
                                </div>
                            </div>

                            <!-- Right Aligned (Current User) -->
                            <div v-if="message.access.fullName === currentUser.fullName" class="flex items-end ml-auto">
                                <div class="bg-orange-500 p-3 rounded-lg shadow-sm max-w-md">
                                    <p class="font-medium text-xs text-gray-300 mb-2">{{ message.access.fullName }}</p>
                                    <p v-if="message.type === 'text'" class="text-white">{{ message.message }}</p>
                                    <expandable-image v-else :src="message.message" alt="img-pr" class="rounded-lg" />
                                    <span class="block text-xs text-orange-200 mt-1">{{ message.__v }}</span>
                                </div>
                                <div class="w-10 h-10 rounded-full overflow-hidden ml-3">
                                    <img src="https://img.icons8.com/color/96/circled-user-male-skin-type-1-2--v1.png"
                                        alt="User avatar" class="w-full h-full object-cover" />
                                </div>
                            </div>
                        </div>


                    </div>


                </div>

                <!-- Chat Input -->
                <div class="p-4 border-t border-gray-200 bg-white sticky bottom-0 right-0 left-40">
                    <div v-if="name == 'orders'" class="flex items-center">
                        <input v-model="newMessage" type="text" placeholder="Type a message..."
                            class="flex-1 p-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500"
                            @keyup.enter="sendMessage('orders', 'message')" />
                        <button @click="sendMessage(name, 'message')"
                            class="ml-2 p-2 bg-orange-500 text-white rounded-lg hover:bg-orange-600">
                            <i class="material-icons">send</i>
                        </button>
                    </div>

                    <div v-if="name == 'shipping'" class="flex items-center">
                        <input v-model="newMessage" type="text" placeholder="Type a message..."
                            class="flex-1 p-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500"
                            @keyup.enter="sendMessage('shipping', 'message')" />
                        <button @click="sendMessage('shipping', 'message')"
                            class="ml-2 p-2 bg-orange-500 text-white rounded-lg hover:bg-orange-600">
                            <i class="material-icons">send</i>
                        </button>
                    </div>
                </div>
            </div>

        </div>
    </div>
</template>

<script>
export default {
    data() {
        return {
            isSidebarOpen: false,
            name: "orders",
            currentUser: {},
            data: [],
            shippingData: [],
            orders: [],
            shipping: [],
            activeChat: 1,
            loading: false,
            newMessage: "",
            activeOrder: null, 
            newMessage: "",
            activeOrderDetails: {
                messages: [],
                loading: false,
                _id: '',

            },
            activeShippingDetails: {
                messages: [],
                loading: false,
                _id: '',

            },
            pollingInterval: null, // For real-time updates
        };
    },
    computed: {
        ordersWithUnreadMessages() {
            if (!this.data) {
                return [];
            }

            return this.data.reduce((unreadOrders, order) => {
                if (this.hasUnreadMessages(order)) {
                    unreadOrders.push(order._id);
                }
                return unreadOrders;
            }, []);
        },

        shipmentsWithUnreadMessages() {
            if (!this.shippingData) {
                return [];
            }

            return this.shippingData.reduce((unreadShipments, shipment) => {
                if (this.hasUnreadMessages(shipment)) {
                    unreadShipments.push(shipment._id);
                }
                return unreadShipments;
            }, []);
        },
    },
    methods: {
        toggleSidebar() {
            this.isSidebarOpen = !this.isSidebarOpen
        },

        async getUser() {
            const res = await this.$me;
            this.currentUser = res.content;
        },

        async fetchOrders() {
        try {
            this.loading = true;
            const res = await this.$me;
            const user = res.content;

            // Get all orders that have messages
            const orders = await this.$server.getAllOrdersWithMessages('orders', user);
            if(orders) {
                this.orders = orders;
            }
    
        } catch (error) {
            console.error('Component Fetch Error:', error);
        } finally {
            this.loading = false;
        }
        },


        async fetchShipping() {
            try {
                this.loading = true;
                const res = await this.$me;
                const user = res.content;
                this.loading = true;
                const unreadOrders = await this.$server.getAllOrdersWithMessages('shippings', user);
                if (unreadOrders) {
                    this.shipping = unreadOrders;
                }
                this.loading = false;

            } catch (error) {
                console.error('Component Fetch Error:', error);
            } finally {
                this.loading = false;
            }
        },

        hasUnreadMessages(item) {
            // Check if messages exist and is an array
            if (!item.messages || !Array.isArray(item.messages)) {
                return false;
            }

            // Check if any message is unread
            return item.messages.some(message =>
                message.access &&
                message.access.fullName !== this.currentUser.fullName &&
                message.access.vue === false
            );
        },

        async selectOrder(order) {
            this.activeOrder = order._id;

            if (window.innerWidth < 768) { // md breakpoint
                this.isSidebarOpen = false
            }

            this.activeOrderDetails = {
                id: order.id,
                _id: order._id,
                messages: order.messages || [],
            };
            this.activeOrderDetails.messages = this.groupedMessage(this.activeOrderDetails.messages);

            this.scrollToLastMessage();
        },

        async selectShipment(shipment) {
            this.activeOrder = shipment._id;

            if (window.innerWidth < 768) { 
                this.isSidebarOpen = false
            }

            this.activeShippingDetails = {
                id: shipment.orderObjet.id,
                _id: shipment._id,
                messages: shipment.messages || [],
            };
            this.activeShippingDetails.messages = this.groupedMessage(this.activeShippingDetails.messages);

            this.scrollToLastMessage();

        },

        async sendMessage(name, type) {
            let response = null;

            if (!this.newMessage.trim() && type === 'message') {
                this.$toast.warning("Message cannot be empty");
                return false;
            }

            const OrdersData = {
                _id: this.activeOrder,
                id: this.activeOrderDetails.id,
                message: this.newMessage,
                type: type === 'image' ? 'image' : 'text',
            };

            const ShippingData = {
                _id: this.activeOrder,
                message: this.newMessage,
                type: type === 'image' ? 'image' : 'text',
            };

            try {
                if (this.name === 'orders') {
                    response = await this.$server.addMessage('orders', OrdersData);
                } else if (name === 'shipping') {
                    response = await this.$server.addShippingMessage('shippings', ShippingData);
                }

                if (response && response._id) {

                    const updatedMessages = response.messages.map((message, index) => {
                        return {
                            ...message,
                            access: {
                                ...message.access,
                                vue: index !== response.messages.length - 1 
                            }
                        };
                    });

                    if (name === 'orders') {
                        await this.markMessagesAsRead(name, this.activeOrderDetails);
                    }
                    if (name === 'shipping') {
                        await this.markMessagesAsRead(name, this.activeShippingDetails);
                    }

                    if (name === 'orders') {
                        const orderIndex = this.orders.findIndex(o => o.id === this.activeOrderDetails.id);
                        if (orderIndex !== -1) {
                            this.orders[orderIndex] = {
                                ...this.orders[orderIndex],
                                messages: updatedMessages, 
                                lastMessage: this.newMessage,
                                hasUnreadMessages: true
                            };
                        }
                    } else if (name === 'shipping') {
                        const shipmentIndex = this.shipping.findIndex(o => o._id === this.activeShippingDetails._id);
                        if (shipmentIndex !== -1) {
                            this.shipping[shipmentIndex] = {
                                ...this.shipping[shipmentIndex],
                                messages: updatedMessages, 
                                lastMessage: this.newMessage,
                                hasUnreadMessages: true
                            };
                        }
                    }
                    this.newMessage = '';
                    this.$nextTick(() => {
                        this.newMessage = '';
                        this.scrollToLastMessage();
                    });

                } else {
                    this.$toast.warning((response && response.error) || "Failed to send message");
                }

            } catch (error) {
                console.error('Error sending message:', error);
                this.$toast.error("An error occurred while sending the message");
            }
        },

        groupedMessage(data) {
            let finalObj = {}
            if (Array.isArray(data)) {
                data.forEach((msg) => {
                    msg.__v = this.$moment(msg.date).format('HH:mm a');
                    const date = this.$moment(msg.date).lang("fr").format('LL');
                    if (finalObj[date]) {
                        finalObj[date].push(msg);
                    } else {
                        finalObj[date] = [msg];
                    }
                })
                return finalObj;
            } else return data

        },

        async markMessagesAsRead(name, order) {

            try {
                if (!order || !order._id || !order.messages || typeof order.messages !== 'object') {
                    console.error("Invalid order data");
                    return;
                }

                const unreadMessages = [];

                Object.values(order.messages).forEach(dateMessages => {
                    if (Array.isArray(dateMessages)) {
                        const unreadInDate = dateMessages.filter(message => {
                            if (!message || !message.access) {
                                console.warn("Invalid message object:", message);
                                return false;
                            }
                            return !message.access.vue && message.access._id !== this.currentUser._id;
                        });

                        unreadMessages.push(...unreadInDate);
                    }
                });

                if (name === 'orders' && unreadMessages.length > 0) {
                    const dataOrder = await this.$server.markOrderMessagesAsRead("orders", {
                        _id: order._id,
                        messageIds: unreadMessages.map(message => message._id),
                        user: this.currentUser,
                    });


                    if (dataOrder) {
                        order.messages = this.groupedMessage(dataOrder.messages);
                    }
                }

                if (name === 'shipping' && unreadMessages.length > 0) {
                    const dataShipping = await this.$server.markShippingMessagesAsRead("shippings", {
                        _id: order._id,
                        messageIds: unreadMessages.map(message => message._id),
                        user: this.currentUser,
                    });

                    if (dataShipping) {
                        order.messages = this.groupedMessage(dataShipping.messages);
                    }
                }
            } catch (error) {
                console.error("Error marking messages as read:", error);
                this.$toast.warning("Failed to mark messages as read. Please try again.");
            }
        },

        scrollToLastMessage() {
            this.$nextTick(() => {
                const container = this.$refs.msgContainer;
                if (container) {
                    container.style.overflow = 'hidden';
                    container.scrollTop = container.scrollHeight;
                    container.style.overflow = 'auto';
                }
            });
        },

    },

    async mounted() {
        this.fetchOrders();
        this.fetchShipping();
        this.getUser();
    }
};
</script>

<style>
/* Custom CSS for scrollbar */
::-webkit-scrollbar {
    width: 8px;
}

::-webkit-scrollbar-track {
    background: #f1f1f1;
}

::-webkit-scrollbar-thumb {
    background: #888;
    border-radius: 4px;
}

::-webkit-scrollbar-thumb:hover {
    background: #555;
}
</style>